import * as v from 'valibot';

import {
  vcAccountId,
  vcAccountNumber,
  vcBmwOrMiniId,
  vcEnumUpperCase,
  vcGcid,
  vcUcid,
  vcVin,
} from '../../../../../utils/valibot/custom-types';
import { newSchema } from '../../../../../utils/valibot/schema';
import { DmsIdSchema } from '../../../../model/customer-information.model';
import { RetailCrmCustomerMainType, RetailCrmResponseSalutation, RetailCrmResponseTitle } from './get-accounts.model';
import { RetailCrmNationalIdDocumentType } from './national-id-document-type.model';

export enum RetailCrmMainAddress {
  PRIVATE = 'PRIVATE',
  BUSINESS = 'BUSINESS',
}

export enum RetailCrmVatGroup {
  STANDARD = 'STANDARD',
  REDUCED = 'REDUCED',
  NONE = 'NONE',
}

export const RetailCrmAccountDetailsAddressSchema = newSchema(
  'RetailCrmAccountDetailsAddressSchema',
  v.object({
    addressStreet: v.optional(v.string()),
    addressStreetNumber: v.optional(v.string()),
    addressPostalCode: v.optional(v.string()),
    addressCity: v.optional(v.string()),
    addressCountry: v.optional(v.string()),
    addressState: v.optional(v.string()),
    addressComplement: v.optional(v.string()),
    addressLine2: v.optional(v.string()),
    addressPostBox: v.optional(v.string()),
  }),
);

export const RetailCrmAccountConnectionSchema = newSchema(
  'RetailCrmAccountConnectionSchema',
  v.object({
    emailPrivate: v.optional(v.string()),
    mobilePrivate: v.optional(v.string()),
    phonePrivate: v.optional(v.string()),
    emailBusiness: v.optional(v.string()),
    mobileBusiness: v.optional(v.string()),
    phoneBusiness: v.optional(v.string()),
  }),
);

export const RetailCrmAccountNameSchema = newSchema(
  'RetailCrmAccountNameSchema',
  v.object({
    salutation: v.optional(v.enum(RetailCrmResponseSalutation)),
    title: v.optional(v.enum(RetailCrmResponseTitle)),
    firstName: v.string(),
    middleName: v.optional(v.string()),
    lastName: v.string(),
    nameInitials: v.optional(v.string()),
    personBirthdate: v.optional(v.string()),
    suffix: v.optional(v.string()),
  }),
);

export const RetailCrmAccountOkuSchema = newSchema(
  'RetailCrmAccountOkuSchema',
  v.object({
    owner: v.optional(v.boolean()),
    keeper: v.optional(v.boolean()),
    user: v.optional(v.boolean()),
  }),
);

export const RetailCrmAccountVehicleInformationSchema = newSchema(
  'RetailCrmAccountVehicleInformationSchema',
  v.object({
    VIN: v.optional(vcVin), // The VIN is optional on Retail CRM’s side, but we filter out these vehicles.
    registrationNumber: v.optional(v.string()),
    name: v.optional(v.string()),
    oku: RetailCrmAccountOkuSchema,
  }),
);

export const RetailCrmAccountIndividualDetailsSchema = newSchema(
  'RetailCrmAccountIndividualDetailsSchema',
  v.object({
    selfEmployed: v.boolean(),
  }),
);

export const RetailCrmContactChannelsSchema = newSchema(
  'RetailCrmContactChannelsSchema',
  v.object({
    email: v.boolean(),
    inCar: v.boolean(),
    letter: v.boolean(),
    phoneMessenger: v.boolean(),
  }),
);

export const RetailCrmPrivacyConsentSchema = newSchema(
  'RetailCrmPrivacyConsentSchema',
  v.object({
    consentId: v.optional(v.string()),
    consentDate: v.optional(v.pipe(v.string(), v.isoDate())), // ISO date string format YYYY-MM-DD
    dataProcessing: v.optional(v.boolean()),
    customerProfile: v.optional(v.boolean()),
    contactChannels: v.optional(RetailCrmContactChannelsSchema),
    legalEntities: v.optional(v.array(v.string())),
  }),
);

export const RetailCrmAccountDetailsResponseSchema = newSchema(
  'RetailCrmAccountDetailsResponseSchema',
  v.object({
    accountNumber: vcAccountNumber,
    gcid: v.optional(vcGcid),
    accountId: v.optional(vcAccountId),
    ucid: v.optional(vcUcid),
    customerMainType: v.optional(v.enum(RetailCrmCustomerMainType)),
    nationality: v.optional(v.string()),
    privateAddress: v.optional(RetailCrmAccountDetailsAddressSchema),
    businessAddress: v.optional(RetailCrmAccountDetailsAddressSchema),
    mainAddress: v.optional(v.enum(RetailCrmMainAddress)),
    connection: RetailCrmAccountConnectionSchema,
    name: RetailCrmAccountNameSchema,
    vehicleInformation: v.optional(v.array(RetailCrmAccountVehicleInformationSchema)),
    bmwOrMiniId: v.optional(vcBmwOrMiniId),
    dmsIds: v.optional(v.array(DmsIdSchema)),
    individualDetails: v.optional(RetailCrmAccountIndividualDetailsSchema),
    inSyncWithDirectSales: v.optional(v.boolean()),
    privacyConsent: v.optional(RetailCrmPrivacyConsentSchema),
    taxNumber: v.optional(v.string()),
    taxNumberCountry: v.optional(v.string()),
    nationalId1: v.optional(v.string()),
    nationalId1DocumentType: v.optional(v.enum(RetailCrmNationalIdDocumentType)),
    vatGroup: v.optional(vcEnumUpperCase(RetailCrmVatGroup)),
    isInsolvent: v.optional(v.boolean()),
    placeOfBirth: v.optional(v.string()),
    description: v.optional(v.string()),
    correspondenceLanguage: v.optional(v.string()),
  }),
);

export type RetailCrmAccountDetailsAddress = v.InferOutput<typeof RetailCrmAccountDetailsAddressSchema>;
export type RetailCrmAccountConnection = v.InferOutput<typeof RetailCrmAccountConnectionSchema>;
export type RetailCrmAccountName = v.InferOutput<typeof RetailCrmAccountNameSchema>;
export type RetailCrmAccountOku = v.InferOutput<typeof RetailCrmAccountOkuSchema>;
export type RetailCrmAccountVehicleInformation = v.InferOutput<typeof RetailCrmAccountVehicleInformationSchema>;
export type RetailCrmAccountIndividualDetails = v.InferOutput<typeof RetailCrmAccountIndividualDetailsSchema>;
export type RetailCrmContactChannels = v.InferOutput<typeof RetailCrmContactChannelsSchema>;
export type RetailCrmPrivacyConsent = v.InferOutput<typeof RetailCrmPrivacyConsentSchema>;
export type RetailCrmAccountDetailsResponse = v.InferOutput<typeof RetailCrmAccountDetailsResponseSchema>;
